import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Balance from '@mui/icons-material/Balance';
import Explore from '@mui/icons-material/Explore';
import Gavel from '@mui/icons-material/Gavel';
import School from '@mui/icons-material/School';
import Star from '@mui/icons-material/Star';
import TrendingUp from '@mui/icons-material/TrendingUp';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import BusinessCenter from '@mui/icons-material/BusinessCenter';
import VolunteerActivism from '@mui/icons-material/VolunteerActivism';
import LiveTv from '@mui/icons-material/LiveTv';
import { LEGALMENTE_LOGO_SRC } from '../brandAssets';
import { LEGALMENTE_EJES_LISTA, LEGALMENTE_PARRAFO_ALFABETIZACION } from './legalmenteHeroCopy';

const iconSx = { fontSize: 28, color: 'inherit' };

const LineRow = ({ icon, children, sx = {} }) => (
  <Box display="flex" alignItems="flex-start" gap={1.75} sx={{ mb: 2, ...sx }}>
    <Box
      sx={{
        color: (t) => t.palette.secondary.main,
        display: 'flex',
        flexShrink: 0,
        mt: 0.35,
        opacity: 0.95,
      }}
    >
      {React.cloneElement(icon, { sx: iconSx })}
    </Box>
    <Box flex={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  </Box>
);

const ejesIconos = [
  <TrendingUp key="t" />,
  <EmojiEvents key="e" />,
  <BusinessCenter key="b" />,
  <VolunteerActivism key="v" />,
];

/**
 * Hero en dos columnas: panel verde + logo (izq.) · texto con iconos y títulos dorados (der.).
 */
const HeroMediaKitCover = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), { defaultMatches: true });
  const gold = theme.palette.secondary.main;
  const deep = theme.palette.primary.main;
  const deepDark = theme.palette.primary.dark;
  const paperBg = theme.palette.alternate?.main || '#f4f6f4';

  return (
    <Box
      sx={{
        borderRadius: isSm ? 4 : 0,
        boxShadow: 3,
        overflow: 'hidden',
        border: `1px solid rgba(196, 164, 93, 0.35)`,
      }}
    >
      <Grid container alignItems="stretch">
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: 'flex',
            borderRight: { md: `2px solid rgba(196, 164, 93, 0.45)` },
            borderBottom: { xs: `2px solid rgba(196, 164, 93, 0.35)`, md: 'none' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 220, sm: 260, md: 'auto' },
              py: { xs: 4, md: 5 },
              px: 3,
              position: 'relative',
              background: `
                radial-gradient(ellipse 100% 80% at 50% 20%, rgba(196, 164, 93, 0.18) 0%, transparent 55%),
                linear-gradient(168deg, ${deepDark} 0%, ${deep} 42%, #0f2418 100%)
              `,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 220,
                borderRadius: 2,
                border: `2px solid ${gold}`,
                p: 2.5,
                background: `radial-gradient(circle at 40% 30%, rgba(196,164,93,0.12) 0%, transparent 55%), rgba(0,0,0,0.2)`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              }}
            >
              <Box
                component="img"
                src={LEGALMENTE_LOGO_SRC}
                alt="Legalmente en Familia"
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.45))',
                }}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              height: '100%',
              px: { xs: 2.25, sm: 3, md: 3.5 },
              py: { xs: 2.5, sm: 3.5, md: 4 },
              bgcolor: paperBg,
              color: 'text.primary',
            }}
          >
            <LineRow icon={<Balance />}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 700,
                  color: gold,
                  lineHeight: 1.3,
                }}
              >
                Derechos que unen, leyes que protegen.
              </Typography>
            </LineRow>

            <LineRow icon={<Explore />}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 700,
                  color: gold,
                  lineHeight: 1.35,
                }}
              >
                La brújula legal de la familia venezolana
              </Typography>
            </LineRow>

            <LineRow icon={<Gavel />}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontStyle: 'italic',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 600,
                  color: gold,
                  lineHeight: 1.45,
                }}
              >
                Informar para salvar y proteger.
              </Typography>
            </LineRow>

            <LineRow icon={<School />}>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {LEGALMENTE_PARRAFO_ALFABETIZACION}
              </Typography>
            </LineRow>

            <LineRow icon={<Star />} sx={{ mb: 1.25 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: gold,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: '0.95rem',
                }}
              >
                Los 4 ejes de una líder de opinión clave
              </Typography>
            </LineRow>

            {LEGALMENTE_EJES_LISTA.map((linea, i) => (
              <LineRow key={linea} icon={ejesIconos[i]} sx={{ mb: 1.35 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                  {linea}
                </Typography>
              </LineRow>
            ))}

            <LineRow icon={<LiveTv />} sx={{ mb: 1.5, mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                Presentado por Leila Davogustto Luna en TNO Network · Lunes 12:00 PM · @legalmenteenfamilia
              </Typography>
            </LineRow>

            <Box sx={{ pl: { xs: 0, sm: 5 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link
                href="https://www.tnonetwork.com/show/legalmenteenfamilia"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: gold, fontWeight: 600, fontSize: '0.95rem', width: 'fit-content' }}
                underline="hover"
              >
                Ver programa en TNO Network
              </Link>
              <Link
                href="https://www.instagram.com/legalmenteenfamilia/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.95rem', width: 'fit-content' }}
                underline="hover"
              >
                @legalmenteenfamilia
              </Link>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeroMediaKitCover;
